import React, { useCallback, useMemo } from 'react';
import { Link, Route, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import classNames from 'classnames';
import { closest } from 'fastest-levenshtein';

import MediaQuery from 'react-responsive';
import TemporaryRedirect from 'cityads-v3/components/Router/TemporaryRedirect';
import Typography from '@mui/material/Typography'
import PermanentRedirect from 'cityads-v3/components/Router/PermanentRedirect';

import ForgotDialog from 'cityads-v3/containers/Auth/ForgotDialog';
import LoginDialog from 'cityads-v3/containers/Auth/LoginDialog';
import RegistrationDialog from 'cityads-v3/containers/Auth/RegistrationDialog';
import ResetDialog from 'cityads-v3/containers/Auth/ResetDialog';
import WelcomeDialog from 'cityads-v3/containers/Auth/WelcomeDialog';

import HeaderCredits from './HeaderCredits'
import HeaderManager from './HeaderManager';
import HeaderTheme from './HeaderTheme'
import HeaderUser from './HeaderUser';

import { authRegexp } from 'cityads-v3/utils/authUtils';
import { breakpoints } from 'cityads-v3/utils/styleUtils';
import classes from './HeaderToolbar.scss';
import { RootState } from 'cityads-v3/types/reducer';
import HeaderLanguageMenu from './HeaderLanguageMenu';

const serverMediaDefaults = {
  width: breakpoints.md,
};

export const MENU_LINKS = [
  {
    to: '/main/about',
    title: 'header.about',
    icon: 'help_outline'
  },
  {
    to: '/publisher/offers',
    title: 'header.offers',
    icon: 'stars'
  },
  {
    to: '/main/news',
    title: 'header.blog',
    requireLanguages: ['ru', 'en'],
    icon: 'library_books'
  },
];

const ALLOWED_REGISTRATION_TAB_VALUES = ['publisher', 'advertiser', 'blogger'];

export default function HeaderToolbar() {
  const { t } = useTranslation(['main'])
  const location = useLocation()
  const history = useHistory()

  const language = useSelector((state: RootState) => state.language.language)
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn)
  const user = useSelector((state: RootState) => state.auth.user)

  const locationWithoutAuth = useMemo(() => ({
    ...location,
    pathname: location.pathname.replace(authRegexp, '')
  }), [location])

  const authLocation = useMemo(() => ({
    ...location,
    pathname: location.pathname.replace(authRegexp, '') + '/auth'
  }), [location])

  const LINKS = useMemo(() => MENU_LINKS
    .filter(({ requireLanguages }) => {
      if (requireLanguages && !requireLanguages.includes(language)) {
        return false;
      }
      return true;
    })
    .map(({ to, title }) => (
      <Typography
        key={to}
        component={Link}
        color="textPrimary"
        className={classNames(classes.item, classes.itemLink)}
        to={to}
      >
        {t(title)}
      </Typography>
    )), [language, t])

  const hideAuthForm = useCallback(() => {
    history.push(locationWithoutAuth);
  }, [history, locationWithoutAuth])

  const handleAuthTabChange = useCallback((tab: string) => {
    history.replace({
      ...location,
      pathname: location.pathname.replace(authRegexp, (a, type) => `/${type}/${tab}`)
    });
  }, [history, location])

  return (
    <div className={classes.headerToolbar}>
      <MediaQuery key="start" maxWidth={breakpoints.sm} device={window ? null : serverMediaDefaults}>
        {isMobile => (
          <div className={classes.wideToolbar}>
            {(isLoggedIn || isMobile) ? false : LINKS}

            <HeaderManager />
            <HeaderCredits />
            <HeaderUser
              authLocation={authLocation}
            />

            <HeaderLanguageMenu className={classNames(classes.item, classes.itemLang)}/>

            <HeaderTheme />
          </div>
        )}
      </MediaQuery>

      {user ?
        (
          <>
            <Route path="*/auth" render={() => (
              <TemporaryRedirect to={location.pathname.replace(authRegexp, '')} />
            )} />
            <Route path="*/register" render={() => (
              <WelcomeDialog
                onRequestClose={hideAuthForm}
                closeLink={locationWithoutAuth}
              />
            )} />
          </>
        ) :
        (
          <>
            <Route path="*/auth" render={() => (
              <LoginDialog
                onRequestClose={hideAuthForm}
                closeLink={locationWithoutAuth}
              />
            )} />
            <Route path="*/register/:tabName?" render={({ match }) => (
              ALLOWED_REGISTRATION_TAB_VALUES.includes(match.params.tabName) ? (
                <RegistrationDialog
                  onRequestClose={hideAuthForm}
                  closeLink={locationWithoutAuth}
                  tab={match.params.tabName}
                  onTabChange={handleAuthTabChange}
                />
              ) : (
                <PermanentRedirect to={location.pathname.replace(authRegexp, `/register/${closest(match.params.tabName ?? '', ALLOWED_REGISTRATION_TAB_VALUES)}`)} />
              )
            )} />
          </>
        )
      }
      <Route path="*/lost" render={({ location }) => {
        const parsedLocation = queryString.parse(location.search);

        return parsedLocation.key ?
          (
            <ResetDialog
              onRequestClose={hideAuthForm}
              closeLink={locationWithoutAuth}
              resetKey={parsedLocation.key}
            />
          ) :
          (
            <ForgotDialog
              onRequestClose={hideAuthForm}
              closeLink={locationWithoutAuth}
            />
          )
      }} />
    </div>
  );
}
